import React from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, startCase } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'

import { IconTriangle, IconTriangleInverted } from '@tabler/icons-react'
import { sentimentLegend, sentimentColors } from '@/utils/constants'
import { getSentimentDisplay } from '@/utils/helpers'
import { sendFullStoryEvent } from '@/utils/fullStoryHelpers'
import { Card } from '@/components/cards/Card'
import { EChart } from '@/components/charts/EChart'
import { nativeTooltip } from '@/components/charts/helpers'
import { LoadingInterstitialCard } from '@/components/loaders/LoadingInterstitialCard'
import { PoweredByAI } from '@/components/ai/PoweredByAI'

import './SentimentModule.scss'

export const SentimentModule = ({ organizationId, lastNDays }) => {
  const { endingSentiment, loading } = useSelector((state) => state.trends)

  if (isEmpty(endingSentiment?.time_series)) {
    return null
  }

  const categories = Object.keys(endingSentiment.sentiments).map((value) => startCase(value))
  const barData = Object.entries(endingSentiment.sentiments)
    .map(([key, value]) => ({
      value: value.count,
      name: startCase(key),
      itemStyle: { color: sentimentLegend[key], borderRadius: [0, 7, 7, 0] },
      call_ids: value.call_ids,
    }))
    .reverse()
  const lineData = endingSentiment.time_series
  const seriesData = lineData.map((d) => [
    moment(d.date).format('MM/DD/YYYY'),
    d.ending_sentiment_avg,
  ])

  const barChartOption = {
    tooltip: nativeTooltip({
      show: true,
      trigger: 'axis',
      formatter: (params) => {
        const point = params[0]

        return `${point.marker} <strong>${point.name}</strong>: ${point.value}`
      },
    }),
    legend: { show: false },
    grid: { left: '4%', right: '5%', bottom: '3%', top: '3%', containLabel: true },
    xAxis: {
      type: 'value',
      boundaryGap: false,
      axisLine: { show: true },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'category',
      inverse: false,
      data: categories,
      axisLabel: { show: false },
      axisTick: { show: false },
    },
    emphasis: { focus: 'series' },
    label: {
      show: true,
      position: [5, -15],
      formatter: (params) => params.name,
      align: 'left',
    },
    series: [{ name: 'Sentiment', type: 'bar', data: barData }],
  }

  const lineChartOption = {
    backgroundColor: 'transparent',
    tooltip: nativeTooltip({
      show: true,
      trigger: 'axis',
      axisPointer: { type: 'line', axis: 'x' },
      formatter: (params) => {
        const point = params[0]
        const date = new Date(point.axisValue)
        const [, sentiment] = point.value

        return `${moment(date).format('MMMM D, YYYY')}
<br/>Sentiment: ${getSentimentDisplay(sentiment)} ${point.marker}`
      },
    }),
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: lastNDays,
      axisLine: { show: true },
      splitLine: { show: false },
    },
    yAxis: {
      boundaryGap: false,
      name: 'Sentiment',
      type: 'value',
      min: 1,
      max: 9,
      show: false,
      interval: 1,
      minInterval: 1,
    },
    visualMap: {
      type: 'continuous',
      show: false,
      min: 1,
      max: 9,
      dimension: 1, // 0 = x, 1 = y
      seriesIndex: 0,
      inRange: { color: sentimentColors },
    },
    series: [
      {
        type: 'line',
        name: 'Sentiment',
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 3 },
        emphasis: { disabled: true },
        data: seriesData,
        zlevel: 1,
        z: 10,
        triggerEvent: true,
      },
    ],
  }

  const handleClick = ({ data }) => {
    const callIds = data.call_ids
    const limitedCallIds = Array.isArray(callIds) ? callIds.slice(0, 400) : callIds

    sendFullStoryEvent('Trends Sentiment Chart clicked', {
      sentimentCategory: data.name || 'Unknown',
      callCount: callIds?.length || 0,
    })

    const queryParams = queryString.stringify({ call_ids: limitedCallIds, organizationId })
    const url = `${window.origin}/explorer?${queryParams}`
    window.open(url)
  }

  return (
    <div data-testid="sentiment-module">
      <Card
        className="mbl"
        data-testid="ending-sentiment-trend-card"
        title="Customer Sentiment"
        compact
      >
        {loading.endingSentiment ? (
          <LoadingInterstitialCard />
        ) : (
          <>
            <div className="flex-align-center medium-gap" data-testid="chart-container">
              <div style={{ width: '33%' }}>
                <EChart option={barChartOption} events={{ click: handleClick }} />
              </div>
              <div className="insights-sentiment-graph-container" style={{ width: '67%' }}>
                <div className="insights-sentiment-graph__positive">
                  <IconTriangle />
                  Positive
                </div>
                <div className="insights-sentiment-graph__negative">
                  <IconTriangleInverted />
                  Negative
                </div>
                <EChart option={lineChartOption} />
              </div>
            </div>
            <PoweredByAI />
          </>
        )}
      </Card>
    </div>
  )
}
